import { FC, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import {
  Alert,
  BookIcon,
  Box,
  Button,
  Column,
  RefreshIcon,
  Row,
  SectionHeading,
  Text,
} from "@hightouch/htui";
import { ComponentType, NodeType, FormkitComponent } from "@hightouch/formkit";
import { JourneySyncConfig } from "src/components/journey/journey-sync-config";
import { SyncTemplateLock } from "src/formkit/components/sync-template-lock/sync-template-lock";
import { Link } from "src/router";

const STATIC_OBJECT = {};

interface JourneySettingsProps {
  name: string;
  isDisabled?: boolean;
  showModeSelection?: boolean;
  showExitConfig?: boolean;
  modeLabel?: string;
  exitConfigLabel?: string;
  showClearable?: boolean;
  onClear?: () => void;
  showLocks?: boolean;
}

export const JourneySettings: FC<JourneySettingsProps> = ({
  name,
  isDisabled = false,
  showClearable = false,
  onClear,
  showLocks = false,
}) => {
  const { watch } = useFormContext();
  const modeValue = watch(`${name}.mode`);
  const exitConfigValue =
    modeValue === "cohort" ? watch(`${name}.exit_config`) : STATIC_OBJECT;

  const hasFieldValues = useMemo(() => {
    const hasModeValue =
      modeValue !== null && modeValue !== undefined && modeValue !== "";
    const hasExitConfigValue =
      modeValue === "cohort" &&
      exitConfigValue &&
      typeof exitConfigValue === "object" &&
      Object.keys(exitConfigValue).length > 0;

    return hasModeValue || hasExitConfigValue;
  }, [modeValue, exitConfigValue]);

  // Create formkit component nodes for the SyncTemplateLock component
  // Map to specific form fields that the lock needs to evaluate
  const journeyNodes = useMemo(
    (): FormkitComponent[] => [
      {
        key: `${name}.mode`,
        component: ComponentType.JourneySettings,
        type: NodeType.Component,
        props: {},
      },
      {
        key: `${name}.exit_config`,
        component: ComponentType.JourneySettings,
        type: NodeType.Component,
        props: {},
      },
    ],
    [name],
  );

  const rightContent = useMemo(() => {
    if (!(showClearable || showLocks)) {
      return null;
    }

    return (
      <Row alignItems="center" gap={1}>
        {showClearable && hasFieldValues && onClear && (
          <Button variant="tertiary" icon={RefreshIcon} onClick={onClear}>
            Clear selection
          </Button>
        )}
        {showLocks && <SyncTemplateLock nodes={journeyNodes} />}
      </Row>
    );
  }, [showClearable, showLocks, hasFieldValues, onClear, journeyNodes]);

  return (
    <Column gap={4} pb={4}>
      <Row justify="space-between" align="center">
        <SectionHeading>Journey Settings</SectionHeading>
        <Link
          isExternal
          href={`${import.meta.env.VITE_DOCS_URL}/customer-studio/journeys`}
        >
          <Row gap={1} align="center">
            <Box as={BookIcon} boxSize={4} />
            <Text color="link.default" size="sm">
              Journey docs
            </Text>
          </Row>
        </Link>
      </Row>
      <Alert
        title="These settings will be ignored when you apply this template to an audience sync."
        variant="inline"
        type="info"
      />
      <JourneySyncConfig
        namePrefix={name}
        isDisabled={isDisabled}
        variant="inline"
        rightContent={rightContent}
      />
    </Column>
  );
};
